/**
 * ProjectCreator.js - Interface de création de projet avec power switches
 */

import "./project-creator.css";
import { createElement } from "../../utils/dom";

export class ProjectCreator {
  constructor(parentElement, options = {}) {
    if (!parentElement) {
      throw new Error("ProjectCreator: parentElement est requis");
    }

    this.parentElement = parentElement;
    this.options = {
      onProjectCreate: options.onProjectCreate || (() => {}),
      onCancel: options.onCancel || (() => {}),
      ...options,
    };

    // État du formulaire
    this.projectConfig = {
      name: "PMXXX",
      fiberType: 24,
      activeTiroirs: [1, 2, 3, 4], // Tous activés par défaut
    };

    this.container = null;
    this.render();
  }

  /**
   * Créer le container du composant
   */
  createContainer() {
    this.container = createElement("div", {
      className: "ui-container-center",
    });

    this.parentElement.appendChild(this.container);
    return this.container;
  }

  /**
   * Calculer le nombre total de fibres
   */
  calculateTotalFibers() {
    const activeTiroirCount = this.projectConfig.activeTiroirs.length;
    const modulesPerTiroir = this.projectConfig.fiberType === 24 ? 6 : 12;
    const fibersPerModule = this.projectConfig.fiberType;

    return activeTiroirCount * modulesPerTiroir * fibersPerModule;
  }

  /**
   * Mettre à jour l'affichage du compteur
   */
  updateFiberCounter() {
    // Méthode supprimée - compteur retiré de l'interface
  }

  /**
   * Gérer le toggle d'un tiroir avec switch
   */
  toggleTiroir(tiroirNum) {
    const isCurrentlyActive = this.projectConfig.activeTiroirs.includes(tiroirNum);

    if (isCurrentlyActive) {
      // Désactiver le tiroir
      this.projectConfig.activeTiroirs = this.projectConfig.activeTiroirs.filter((t) => t !== tiroirNum);
    } else {
      // Activer le tiroir
      this.projectConfig.activeTiroirs.push(tiroirNum);
      this.projectConfig.activeTiroirs.sort();
    }

    // Mettre à jour l'affichage du switch
    this.updateTiroirSwitch(tiroirNum);
    this.validateForm();
  }

  /**
   * Mettre à jour l'affichage d'un switch tiroir
   */
  updateTiroirSwitch(tiroirNum) {
    const switchBtn = this.container.querySelector(`[data-tiroir="${tiroirNum}"]`);
    if (switchBtn) {
      const isActive = this.projectConfig.activeTiroirs.includes(tiroirNum);
      switchBtn.className = `tiroir-power-switch ${isActive ? "power-switch-on" : "power-switch-off"}`;
    }
  }

  /**
   * Valider le formulaire
   */
  validateForm() {
    const isNameValid = this.projectConfig.name.trim().length > 0;
    const hasTiroirs = this.projectConfig.activeTiroirs.length > 0;

    const startBtn = this.container.querySelector("#startProjectBtn");
    if (startBtn) {
      startBtn.disabled = !isNameValid || !hasTiroirs;
    }

    return isNameValid && hasTiroirs;
  }

  /**
   * Rendu du créateur de projet
   */
  render() {
    if (!this.container) {
      this.createContainer();
    }

    this.container.innerHTML = `
      <div class="ui-card ui-card-form">
        <div class="creator-header">
          <h2 class="creator-title">Création d'un nouveau projet</h2>
          <p class="creator-subtitle">
            Configurez les paramètres essentiels de votre projet : titre personnalisé, 
            type de modules (12 ou 24 fibres), et activation/désactivation des tiroirs disponibles 
            selon vos besoins de déploiement.
          </p>
        </div>

        <div class="creator-form">
          <!-- Section Nom du projet -->
          <div class="form-section form-section-inline">
            <label class="ui-label" for="projectName">
              <span class="label-icon">📝</span>
              Nom du projet
            </label>
            <input 
              type="text" 
              id="projectName" 
              class="ui-input"
              value="${this.projectConfig.name}"
              placeholder="Entrez le nom du projet"
              maxlength="50"
            />
          </div>

          <!-- Section Type de modules -->
          <div class="form-section">
            <label class="ui-label">
              <span class="label-icon">⚙️</span>
              Type de modules
            </label>
            <div class="radio-group">
              <label class="radio-option">
                <input 
                  type="radio" 
                  name="fiberType" 
                  value="24"
                  ${this.projectConfig.fiberType === 24 ? "checked" : ""}
                />
                <span class="radio-custom"></span>
                <div class="radio-number">24</div>
                <span class="radio-text">Fibres</span>
                <div class="radio-modules">
                  ${["A", "B", "C", "D", "E", "F"]
                    .map((letter) => `<span class="radio-module">${letter}</span>`)
                    .join("")}
                </div>
              </label>
              <label class="radio-option">
                <input 
                  type="radio" 
                  name="fiberType" 
                  value="12"
                  ${this.projectConfig.fiberType === 12 ? "checked" : ""}
                />
                <span class="radio-custom"></span>
                <div class="radio-number">12</div>
                <span class="radio-text">Fibres</span>
                <div class="radio-modules modules-12">
                  ${["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"]
                    .map((letter) => `<span class="radio-module">${letter}</span>`)
                    .join("")}
                </div>
              </label>
            </div>
          </div>

          <!-- Section Tiroirs avec Power Switches -->
          <div class="form-section">
            <label class="ui-label">
              <span class="label-icon">🗄️</span>
              Tiroirs à activer
            </label>
            <div class="tiroirs-switches-grid">
              ${[1, 2, 3, 4, 5, 6]
                .map((tiroirNum) => {
                  const isActive = this.projectConfig.activeTiroirs.includes(tiroirNum);
                  return `
                  <div class="tiroir-switch-item">
                    <span class="tiroir-label">Tiroir T${tiroirNum}</span>
                    <button 
                      class="tiroir-power-switch ${isActive ? "power-switch-on" : "power-switch-off"}"
                      data-tiroir="${tiroirNum}"
                      type="button"
                    >
                      <div class="power-icon"></div>
                    </button>
                  </div>
                `;
                })
                .join("")}
            </div>
          </div>



          <!-- Actions -->
          <div class="form-actions">
            <button class="ui-btn ui-btn-outline" id="cancelBtn">
              Annuler
            </button>
            <button class="ui-btn ui-btn-primary" id="startProjectBtn">
              Démarrer la saisie
            </button>
          </div>
        </div>
      </div>
    `;

    this.setupEventListeners();
    this.validateForm();
  }

  /**
   * Configurer les événements
   */
  setupEventListeners() {
    // Input nom du projet
    const nameInput = this.container.querySelector("#projectName");
    if (nameInput) {
      nameInput.addEventListener("input", (e) => {
        this.projectConfig.name = e.target.value;
        this.validateForm();
      });
    }

    // Radio buttons type de fibres
    const fiberRadios = this.container.querySelectorAll('input[name="fiberType"]');
    fiberRadios.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        this.projectConfig.fiberType = parseInt(e.target.value);
        this.validateForm();
      });
    });

    // Power switches des tiroirs
    const tiroirSwitches = this.container.querySelectorAll(".tiroir-power-switch");
    tiroirSwitches.forEach((switchBtn) => {
      switchBtn.addEventListener("click", (e) => {
        e.preventDefault();
        const tiroirNum = parseInt(switchBtn.dataset.tiroir);
        this.toggleTiroir(tiroirNum);
      });
    });

    // Boutons d'action
    const cancelBtn = this.container.querySelector("#cancelBtn");
    const startBtn = this.container.querySelector("#startProjectBtn");

    if (cancelBtn) {
      cancelBtn.addEventListener("click", () => {
        this.options.onCancel();
      });
    }

    if (startBtn) {
      startBtn.addEventListener("click", () => {
        if (this.validateForm()) {
          this.options.onProjectCreate(this.projectConfig);
        }
      });
    }
  }

  /**
   * Animer l'apparition
   */
  animate() {
    const container = this.container.querySelector(".ui-card");
    if (container) {
      container.style.opacity = "0";
      container.style.transform = "translateY(20px)";
      container.style.transition = "all 0.6s ease-out";

      requestAnimationFrame(() => {
        container.style.opacity = "1";
        container.style.transform = "translateY(0)";
      });
    }
  }

  /**
   * Obtenir la configuration du projet
   */
  getProjectConfig() {
    return { ...this.projectConfig };
  }

  /**
   * Masquer le créateur
   */
  hide() {
    if (this.container) {
      this.container.style.display = "none";
    }
  }

  /**
   * Afficher le créateur
   */
  show() {
    if (this.container) {
      this.container.style.display = "block";
      this.animate();
    }
  }

  /**
   * Obtenir l'élément DOM du composant
   */
  getElement() {
    return this.container;
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }

  /**
   * Nettoyer les ressources
   */
  destroy() {
    // Démonter le composant
    this.unmount();

    // Nettoyer les références
    this.container = null;
    this.parentElement = null;
  }
}
